import React, { useState, useEffect } from "react";
import { Box, Typography, LinearProgress } from "@mui/material";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import jsPDF from "jspdf";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CustomerTable from "./CustomerTable";
import ActionButtons from "./ActionButtons";
import "./CustomerProfiling.css";
import ProfileModal from "./ProfileModal.js";
import ExtractedData from "../../components/ExtractedData.js";
import "../../components/PdfPreview.css";
import keyMap from "./keyMap";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Function to standardize any list-like structure
const standardizeList = (list, fieldName) => {
  if (!list || !Array.isArray(list)) {
    return list || {};
  }
  if (fieldName === "documents") {
    return list.reduce((acc, doc) => {
      if (doc.type && doc.fileName) {
        acc[doc.type] = doc.fileName;
      }
      return acc;
    }, {});
  }
  if (fieldName === "validationChecks") {
    return list.reduce((acc, check) => {
      if (check.check && check.result !== undefined) {
        acc[check.check] = check.result;
      } else if (check.key && check.value !== undefined) {
        acc[check.key] = check.value;
      }
      return acc;
    }, {});
  }
  if (fieldName === "telephoneNumbers" || fieldName === "documentsAttached") {
    return list.reduce((acc, item, index) => {
      acc[`${fieldName === "telephoneNumbers" ? "number" : "doc"}${index + 1}`] = item;
      return acc;
    }, {});
  }
  return {};
};

// Function to standardize specific fields in a data object only if they exist
const standardizeData = (data) => {
  if (!data) return {};
  const result = { ...data };
  if (data.documents) {
    result.documents = standardizeList(data.documents, "documents");
  }
  if (data.validationChecks) {
    result.validationChecks = standardizeList(data.validationChecks, "validationChecks");
  }
  if (data.telephoneNumbers) {
    result.telephoneNumbers = standardizeList(data.telephoneNumbers, "telephoneNumbers");
  }
  if (data.documentsAttached) {
    result.documentsAttached = standardizeList(data.documentsAttached, "documentsAttached");
  }
  return result;
};

// Function to format labels (reused from ProfileModal)
const formatLabel = (key) => {
  if (keyMap[key]) return keyMap[key];
  for (const label of Object.keys(keyMap)) {
    if (keyMap[label].some((mappedKey) => mappedKey.includes('.') && mappedKey.split('.')[0] === key)) {
      return key;
    }
  }
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .replace(/Of/g, "of")
    .replace(/No/g, "No")
    .trim();
};

const CustomerProfiling = ({ theme = "dark" }) => {
  const [entities, setEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [userRole, setUserRole] = useState("normal");
  const [file, setFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [referenceId, setReferenceId] = useState(null);
  const [pollingProgress, setPollingProgress] = useState(0);
  const [extractionComplete, setExtractionComplete] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEntitiesLoading, setIsEntitiesLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  // Prevent default drag and drop behavior outside the drag area
  useEffect(() => {
    const preventDefaultDrag = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const preventDefaultDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener("dragover", preventDefaultDrag);
    document.addEventListener("drop", preventDefaultDrop);

    return () => {
      document.removeEventListener("dragover", preventDefaultDrag);
      document.removeEventListener("drop", preventDefaultDrop);
    };
  }, []);

  const fetchEntities = async () => {
    try {
      setIsEntitiesLoading(true);
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found.");
      const response = await axios.get(
        `${API_BASE_URL}/exiting_bankaccounts`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const validEntities = response.data.map((entity, index) => {
        const standardizedInitialData = standardizeData(entity.initial_data);
        const standardizedFinalizedData = standardizeData(entity.finalized_data);
        const standardizedExtractedData = standardizeData(entity.extracted_data);
        return {
          id: entity.id || `temp-${Date.now() + index}`,
          applicant_name: String(
            entity.finalized_data?.fullName ||
            entity.initial_data?.fullName ||
            "Unknown"
          ),
          account_no: String(
            entity.finalized_data?.titleOfAccount ||
            entity.initial_data?.titleOfAccount ||
            "-"
          ),
          branch: String(
            entity.finalized_data?.branch || entity.initial_data?.branch || ""
          ),
          riskRating: entity.finalized_data?.riskRating || "Low",
          documentId: entity.reference_id || "N/A",
          initial_data: standardizedInitialData,
          finalized_data: standardizedFinalizedData,
          extracted_data: standardizedExtractedData,
          ...entity,
        };
      });
      setEntities(validEntities);
      setIsEntitiesLoading(false);
    } catch (err) {
      console.error("Failed to fetch entities:", err);
      setEntities([]);
      setIsEntitiesLoading(false);
      toast.error("Failed to fetch profiles: " + (err.response?.data?.message || err.message), {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: { background: theme === "dark" ? "#f8d7da" : "#fee2e2", color: "#721c24", borderRadius: "8px", fontSize: "14px" },
      });
    }
  };

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("No authentication token found.");
        const response = await axios.get(`${API_BASE_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserRole(response.data.is_superuser === "True" ? "admin" : "normal");
      } catch (err) {
        setUserRole("normal");
        console.error("Failed to fetch user role:", err);
        toast.error("Failed to fetch user role. Defaulting to normal user.", {
          position: "bottom-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          style: { background: theme === "dark" ? "#f8d7da" : "#fee2e2", color: "#721c24", borderRadius: "8px", fontSize: "14px" },
        });
      }
    };
    fetchUserRole();
  }, [theme]);

  useEffect(() => {
    fetchEntities();
  }, []);

  useEffect(() => {
    let pollingInterval = null;
    if (referenceId) {
      setPollingProgress((prev) => Math.min(prev + 10, 90));
      pollingInterval = setInterval(async () => {
        try {
          const token = localStorage.getItem("authToken");
          if (!token) throw new Error("No authentication token found");
          const response = await axios.get(
            `${API_BASE_URL}/bank-account-opening/status/${referenceId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const status = response.data.status;
          setPollingProgress((prev) => Math.min(prev + 10, 90));
          if (status === "extracted") {
            clearInterval(pollingInterval);
            setPollingProgress(100);
            setExtractionComplete(true);
            setReferenceId(null);
            if (response.data.extracted_data) {
              const standardizedExtractedData = standardizeData(response.data.extracted_data);
              setExtractedData(standardizedExtractedData);
            } else {
              setExtractedData({ message: "No data extracted." });
              toast.warn("The extraction completed, but no data was found.", {
                position: "bottom-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                style: { background: theme === "dark" ? "#fff3cd" : "#fefcbf", color: "#856404", borderRadius: "8px", fontSize: "14px" },
              });
            }
          } else if (status === "failed") {
            throw new Error("Extraction failed on server");
          }
        } catch (error) {
          console.error("Polling error:", error);
          clearInterval(pollingInterval);
          setPollingProgress(0);
          setReferenceId(null);
          setExtractionComplete(false);
          setError(
            "Failed to check extraction status: " + (error.response?.data?.message || error.message)
          );
          toast.error(error.response?.data?.message || "Failed to check extraction status.", {
            position: "bottom-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            style: { background: theme === "dark" ? "#f8d7da" : "#fee2e2", color: "#721c24", borderRadius: "8px", fontSize: "14px" },
          });
        }
      }, 5000);
    }
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, [referenceId, theme]);

  const handleEntitySelect = (entity) => {
    console.log("Selected entity finalized_data:", entity.finalized_data);
    setSelectedEntity(entity);
    setModalOpen(true);
  };

  const handleAttachmentUpload = async (selectedFile) => {
    if (!selectedFile) {
      toast.error("Please select a PDF file.", {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: { background: theme === "dark" ? "#f8d7da" : "#fee2e2", color: "#721c24", borderRadius: "8px", fontSize: "14px" },
      });
      return;
    }
    if (selectedFile.type !== "application/pdf") {
      return;
    }
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setFile(null);
    setPdfUrl(null);
    setExtractedData(null);
    setLoading(false);
    setError(null);
    setReferenceId(null);
    setPollingProgress(0);
    setExtractionComplete(false);
    setIsSaved(false);
    setIsEditing(false);
    const fileObj = {
      id: `temp-${Date.now()}`,
      name: selectedFile.name,
      pdfUrl: URL.createObjectURL(selectedFile),
      documentId: `temp-${Date.now()}`,
      pdfPath: null,
    };
    setFile(fileObj);
    setPdfUrl(fileObj.pdfUrl);
    setLoading(true);
    setPollingProgress(5);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("data", JSON.stringify({}));
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("No authentication token found");
      const response = await axios.post(
        `${API_BASE_URL}/bank-account-opening/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}` },
        }
      );
      const referenceId = response.data.reference_id;
      setReferenceId(referenceId);
      setFile((prev) => ({
        ...prev,
        documentId: referenceId,
      }));
      setLoading(false);
    } catch (err) {
      console.error("Upload error:", err);
      setError(
        "Failed to process PDF: " + (err.response?.data?.message || err.message)
      );
      setLoading(false);
      setFile(null);
      setPdfUrl(null);
      setReferenceId(null);
      setPollingProgress(0);
      setExtractionComplete(false);
      toast.error(err.response?.data?.message || err.message, {
        position: "bottom-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        style: { background: theme === "dark" ? "#f8d7da" : "#fee2e2", color: "#721c24", borderRadius: "8px", fontSize: "14px" },
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("File dropped:", e.dataTransfer.files[0]);
    setDragActive(false);
    const selectedFile = e.dataTransfer.files[0];
    if (selectedFile) {
      handleAttachmentUpload(selectedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragActive(false);
    }
  };

  const handleClickUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/pdf";
    input.multiple = false;
    input.onchange = (e) => {
      const selectedFile = e.target.files[0];
      handleAttachmentUpload(selectedFile);
    };
    input.click();
  };

  const handleJsonChange = (newJson) => {
    setExtractedData(newJson);
    setIsEditing(true);
    if (selectedEntity) {
      setSelectedEntity((prev) => ({
        ...prev,
        extracted_data: newJson,
      }));
    }
  };

  const suggestKeyMatch = (key, knownKeys) => {
    const lowerKey = key.toLowerCase();
    for (const label of Object.keys(keyMap)) {
      for (const mappedKey of keyMap[label]) {
        if (
          lowerKey.includes(mappedKey.toLowerCase()) ||
          mappedKey.toLowerCase().includes(lowerKey)
        ) {
          return label;
        }
      }
    }
    return null;
  };

  const validateSave = () => {
    return true;
  };

  const handleDownloadProfile = () => {
    if (!selectedEntity) {
      return;
    }

    const doc = new jsPDF();
    let yOffset = 40;
    const pageHeight = 280;
    const margin = 20;
    const contentWidth = 170;

    const colors = {
      primary: theme === "dark" ? { r: 102, g: 126, b: 234 } : { r: 31, g: 41, b: 55 },
      text: theme === "dark" ? { r: 255, g: 255, b: 255 } : { r: 31, g: 41, b: 55 },
      lightGray: theme === "dark" ? { r: 150, g: 150, b: 150 } : { r: 107, g: 114, b: 128 },
      border: theme === "dark" ? { r: 102, g: 126, b: 234 } : { r: 229, g: 231, b: 235 },
    };

    const checkPageOverflow = (additionalHeight = 15) => {
      if (yOffset + additionalHeight > pageHeight) {
        doc.addPage();
        addHeader();
        yOffset = 50;
        return true;
      }
      return false;
    };

    const addHeader = () => {
      doc.setFontSize(14);
      doc.setFont("times", "bold");
      doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
      doc.text("BotEye Trade and Finance", margin, 20);
      
      const now = new Date();
      const dateTime = now.toLocaleDateString() + " " + now.toLocaleTimeString();
      doc.setFontSize(10);
      doc.setFont("times", "normal");
      doc.setTextColor(colors.lightGray.r, colors.lightGray.g, colors.lightGray.b);
      doc.text(`Generated: ${dateTime}`, 210 - margin, 20, { align: "right" });
      
      doc.setDrawColor(colors.border.r, colors.border.g, colors.border.b);
      doc.setLineWidth(0.5);
      doc.line(margin, 25, 210 - margin, 25);
    };

    const addFooter = () => {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        
        doc.setDrawColor(colors.border.r, colors.border.g, colors.border.b);
        doc.setLineWidth(0.3);
        doc.line(margin, 285, 210 - margin, 285);
        
        doc.setFontSize(8);
        doc.setFont("times", "normal");
        doc.setTextColor(colors.lightGray.r, colors.lightGray.g, colors.lightGray.b);
        
        doc.text("Confidential Document", margin, 292);
        doc.text(`Page ${i} of ${pageCount}`, 210 - margin, 292, { align: "right" });
      }
    };

    const addSectionHeader = (title, estimatedSectionHeight = 50) => {
      if (yOffset + 20 > pageHeight) {
        doc.addPage();
        addHeader();
        yOffset = 50;
      }
      
      doc.setFontSize(11);
      doc.setFont("times", "bold");
      doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
      doc.text(title, margin, yOffset);
      
      doc.setDrawColor(colors.primary.r, colors.primary.g, colors.primary.b);
      doc.setLineWidth(0.5);
      doc.line(margin, yOffset + 2, margin + 60, yOffset + 2);
      
      yOffset += 15;
    };

    const addField = (label, value, isHighlight = false) => {
      checkPageOverflow();
      
      const labelWidth = 70;
      const valueWidth = 95;
      
      doc.setFontSize(10);
      doc.setFont("times", "bold");
      doc.setTextColor(colors.primary.r, colors.primary.g, colors.primary.b);
      
      const labelText = `${label}:`;
      const splitLabel = doc.splitTextToSize(labelText, labelWidth);
      doc.text(splitLabel, margin, yOffset);
      
      doc.setFont("times", isHighlight ? "bold" : "normal");
      doc.setTextColor(colors.text.r, colors.text.g, colors.text.b);
      
      const displayValue = value !== undefined && value !== null && value !== "" 
        ? String(value) 
        : "-";
      
      const splitText = doc.splitTextToSize(displayValue, valueWidth);
      
      if (splitText.length > 1) {
        splitText.forEach((line, index) => {
          if (index > 0) checkPageOverflow();
          doc.text(line, margin + 75, yOffset + (index * 5));
          if (index > 0) yOffset += 5;
        });
      } else {
        doc.text(splitText[0], margin + 75, yOffset);
      }
      
      yOffset += 8;
    };

    const estimateSectionHeight = (data) => {
      let estimatedHeight = 20;
      const valueWidth = 95;
      
      Object.entries(data).forEach(([key, value]) => {
        if (typeof value !== "object" || value === null || Array.isArray(value)) {
          const displayValue = value !== undefined && value !== null && value !== "" 
            ? String(value) 
            : "-";
          const splitText = doc.splitTextToSize(displayValue, valueWidth);
          estimatedHeight += 8 + (splitText.length > 1 ? (splitText.length - 1) * 5 : 0);
        } else {
          estimatedHeight += 20;
          estimatedHeight += estimateSectionHeight(value);
        }
      });
      return estimatedHeight;
    };

    const formatLabel = (key) => {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, l => l.toUpperCase());
    };

    const renderNestedFields = (data, sectionTitle, depth = 0) => {
      if (!data || typeof data !== "object" || Array.isArray(data)) return;
      
      const hasContent = Object.entries(data).some(([key, value]) => {
        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          return Object.keys(value).length > 0;
        }
        return value !== undefined && value !== null && value !== "";
      });
      
      if (!hasContent) return;
      
      addSectionHeader(sectionTitle);
      
      Object.entries(data).forEach(([key, value]) => {
        if (typeof value === "object" && value !== null && !Array.isArray(value)) {
          renderNestedFields(value, formatLabel(key), depth + 1);
        } else {
          const label = formatLabel(key);
          addField(label, value, false);
        }
      });
      
      yOffset += 5;
    };

    addHeader();

    const rawData = selectedEntity.finalized_data || selectedEntity.extracted_data || selectedEntity.initial_data || {};
    
    const data = {
      ...rawData,
      applicant_name: selectedEntity.applicant_name || "Unknown",
      transaction: rawData.transaction || "-",
      status: rawData.status || "-",
    };

    renderNestedFields(data, "Profile Details");

    addFooter();

    const timestamp = new Date().toISOString().slice(0, 10);
    const applicantName = selectedEntity.applicant_name || "Unknown";
    const filename = `Profile_${applicantName.replace(/[^a-zA-Z0-9]/g, '_')}_${timestamp}.pdf`;
    doc.save(filename);
  };

  const truncateFileName = (name, maxLength = 25) => {
    return name.length <= maxLength
      ? name
      : name.substring(0, maxLength - 3) + "...";
  };

  const allAttachments = file
    ? [
        {
          id: file.id,
          name: file.name,
          url: file.pdfUrl,
          documentId: file.documentId,
          pdfPath: file.pdfPath,
        },
      ]
    : [];

  return (
    <Box className={`customer-profiling-container ${theme}-theme`} sx={{ minHeight: "100vh" }}>
      <Box className="customer-profiling-content card">
        <Box className="customer-upload-section">
          <Typography variant="h6" className="customer-section-title">
            Upload Bank Account Opening Form
          </Typography>
          <Box
            className={`customer-drag-area ${dragActive ? "active" : ""}`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClickUpload}
          >
            <Typography variant="body2" className="customer-drag-text">
              Click or Drag & Drop to Upload PDF
            </Typography>
          </Box>
          {allAttachments.length > 0 && (
            <Box className="customer-uploaded-file-section">
              <Typography variant="subtitle1" className="customer-section-subtitle">
                Uploaded File
              </Typography>
              {allAttachments.map((attachment) => (
                <Box key={attachment.id} className="customer-uploaded-file card">
                  <UploadFileIcon className="customer-upload-icon" />
                  <Typography variant="caption" className="customer-file-name">
                    {truncateFileName(attachment.name)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          {pollingProgress > 0 && (
            <Box className="customer-progress-section">
              <LinearProgress
                variant="determinate"
                value={pollingProgress}
                className="customer-progress-bar"
              />
              <Typography variant="body2" className="customer-progress-text">
                {pollingProgress === 100
                  ? "Extraction Complete"
                  : `Extracting Data: ${pollingProgress}%`}
              </Typography>
            </Box>
          )}
          <ExtractedData
            file={file}
            pdfUrl={pdfUrl}
            loading={loading}
            error={error}
            extractedData={extractedData}
            handleJsonChange={handleJsonChange}
            userRole={userRole}
            isSaved={isSaved}
            fromCustomerProfiling={true}
            onEditChange={setIsEditing}
            theme={theme}
          />
          {file && !loading && !isSaved && (
            <ActionButtons
              file={file}
              extractedData={extractedData}
              extractionComplete={extractionComplete}
              setFile={setFile}
              setPdfUrl={setPdfUrl}
              setExtractedData={setExtractedData}
              setLoading={setLoading}
              setError={setError}
              setReferenceId={setReferenceId}
              setPollingProgress={setPollingProgress}
              setExtractionComplete={setExtractionComplete}
              setIsSaved={setIsSaved}
              fetchEntities={fetchEntities}
              pdfUrl={pdfUrl}
              setEntities={setEntities}
              validateSave={validateSave}
              theme={theme}
            />
          )}
        </Box>
        <CustomerTable
          entities={entities}
          isEntitiesLoading={isEntitiesLoading}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleEntitySelect={handleEntitySelect}
          theme={theme}
        />
        <ProfileModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          selectedEntity={selectedEntity}
          userRole={userRole}
          handleDownloadProfile={handleDownloadProfile}
          theme={theme}
        />
      </Box>
    </Box>
    
  );
};

export default CustomerProfiling;